import Autocomplete from "@mui/material/Autocomplete"
import {
  getFieldNameParts,
  getParentName,
  isAutocompleteFieldSchema,
  useField,
  useForm,
} from "@w3rone/json-schema-form"
import type { FieldComponentProps } from "@w3rone/json-schema-form"
import { useAsyncEffect } from "@react-hook/async"
import { fetch } from "app/api"
import { useRouter } from "app/components/RouterProvider"
import { TextField } from "app/components/TextField"
import * as R from "remeda"
import { MaterialIcon } from "app/components/MaterialIcon"
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown"

export const AutocompleteField = ({
  //value: valueProps = { [keywordFieldName]: "" },
  name,
}: FieldComponentProps) => {
  const {
    value: valueProps = { [keywordFieldName]: "" },
    schema,
    label,
    description,
    id,
    errors,
    onChange,
  } = useField(name)

  if (!isAutocompleteFieldSchema(schema)) throw new Error("Bad schema type")

  const value = valueProps as NonNullable<AutocompleteFieldValue>
  const router = useRouter()
  const form = useForm()

  const request = useAsyncEffect<AutocompleteResult[]>(async () => {
    if (value.ac_keyword === "") {
      return []
    }

    let params = null

    if (schema.options.autocomplete.params) {
      const parentName = getParentName(name)

      params = R.mapValues(schema.options.autocomplete.params, (value) => {
        const valueArray = value.split(".")
        const fieldName = `${parentName}${valueArray.map((item) => `[${item}]`).join("")}`

        const fieldValue = getByPath(
          form.value,
          transformStringToArray(fieldName),
        )

        return fieldValue
      })
    }

    const routeParams = {
      entity: schema.options.autocomplete.entity,
      keyword: value.ac_keyword,
      params: params,
    }

    const response = await fetch(
      router.generate("app_api_autocomplete", routeParams),
    )

    const data = await response.json()

    return data
  }, [value.ac_keyword])

  const fieldNameParts = getFieldNameParts(name)

  if (!fieldNameParts) {
    console.error(`Impossible to get field name parts for field ${name}`)
    return null
  }

  const entityFieldName = R.last(fieldNameParts)

  if (!entityFieldName) {
    throw new Error("Can't get entity field name")
  }

  return (
    <div>
      <Autocomplete
        popupIcon={
          schema.properties?.ac_keyword?.options.attr?.icon ? (
            <MaterialIcon
              icon={schema.properties.ac_keyword.options.attr.icon}
            />
          ) : (
            <KeyboardArrowDown fontSize="large" />
          )
        }
        sx={
          schema.properties?.ac_keyword?.options.attr?.icon
            ? { "& .MuiAutocomplete-popupIndicator": { transform: "none" } }
            : undefined
        }
        disabled={schema.readOnly}
        renderInput={(params) => {
          return (
            <TextField
              label={label}
              name={`${name}[${keywordFieldName}]`}
              description={description}
              errors={errors}
              id={id}
              inputProps={params.slotProps.htmlInput}
              {...params.slotProps.input}
              onChange={(e) =>
                onChange?.({
                  ...(value as AutocompleteFieldValue),
                  [keywordFieldName]: e.target.value,
                })
              }
            />
          )
        }}
        options={request.value || []}
        inputValue={value.ac_keyword || ""}
        loading={request.status === "loading"}
        getOptionLabel={(option) => option.name}
        filterOptions={(x) => x}
        onChange={(_event, selectedOption) => {
          onChange?.({
            [keywordFieldName]: selectedOption ? selectedOption.name : "",
            [entityFieldName]: selectedOption ? String(selectedOption.id) : "",
          })
        }}
        isOptionEqualToValue={(option, value) =>
          option.id === value.id && option.name === value.name
        }
      />
      <input
        type="hidden"
        name={`${name}[${entityFieldName}]`}
        value={value.ac_keyword ? value[entityFieldName] : ""}
        onChange={R.doNothing}
      />
      {schema.readOnly ? (
        <input
          type="hidden"
          name={`${name}[${keywordFieldName}]`}
          defaultValue={value.ac_keyword || ""}
        />
      ) : null}
    </div>
  )
}

const keywordFieldName = "ac_keyword"

interface AutocompleteResult {
  id: number
  name: string
}

type AutocompleteFieldValue =
  | {
      ac_keyword: string
      [key: string]: string
    }
  | undefined

function transformStringToArray(input: string): string[] {
  // This regular expression finds all sequences of one or more characters
  // that are not '[' or ']'.
  const regex = /[^[\]]+/g

  // The match function returns an array of matches or null.
  const matches = input.match(regex)

  // We filter out any empty strings that might result from inputs like "[]"
  // and return an empty array if no matches are found.
  return matches ? matches.filter(Boolean) : []
}

function getByPath<T>(obj: T, path: (string | number)[]): unknown {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return path.reduce((acc, key) => (acc as any)?.[key], obj)
}
